/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Grid, IconButton, Popover, TextField, Typography } from '@material-ui/core';
import { ProfileViewModel } from './ProfileViewModel';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ProfileViewStyles from './ProfileViewStyles';
import { FormattedMessage } from 'react-intl';
import { useViewModel } from '../../../hooks/useViewModel';
import { useHistory } from 'react-router-dom';
import { Pages } from '../../../navigation/Pages';

const ProfileView = observer((): ReactElement => {
	const viewModel: ProfileViewModel = useViewModel(ProfileViewModel);
	const classes = ProfileViewStyles();
	const router = useHistory();

	const imgSrc = viewModel.getProfileImage();

	return (
		<>
			<Button variant='outlined' className={classes.profileButton} onClick={viewModel.openPopover}>
				<Typography variant='h2'>{viewModel.getUsername()}</Typography>
				<ArrowDropDownIcon />
			</Button>
			<Popover
				open={viewModel.isOpen()}
				anchorEl={viewModel.getAnchorEl()}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				onClose={() => {
					viewModel.closePopover();
					viewModel.setIsInput(false);
				}}
				classes={{ paper: classes.popover }}>
				<Grid item xs={12} className={classes.container}>
					<Grid item xs={4} className={classes.imageContainer}>
						<IconButton className={classes.iconButton} component='label'>
							<img src={imgSrc} alt='Profile Image' className={classes.profileImage} />
							<img src='/img/editIcon.svg' alt='Edit Icon' className={classes.profileImageIcon} />
							<input
								type='file'
								hidden
								onChange={(event) => {
									viewModel.updateImage(event);
								}}
							/>
						</IconButton>
					</Grid>
					<Grid item className={classes.nameContainer}>
						{viewModel.model.isInput ? (
							<Grid item className={classes.container}>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										viewModel.updateName();
										viewModel.setIsInput(false);
									}}>
									<TextField
										InputProps={{
											classes: {
												input: classes.input,
											},
										}}
										onChange={(event) => {
											viewModel.changeName(event);
										}}
										className={classes.textField}
										placeholder={viewModel.getUsername()}
										autoFocus={true}
									/>
									<Button type='submit' className={classes.submitButton}>
										<FormattedMessage
											id={viewModel.model.name === viewModel.getUsername() ? 'general.cancel' : 'general.change'}
										/>
									</Button>
								</form>
							</Grid>
						) : (
							<Grid item className={classes.container}>
								<Typography variant='h2' className={classes.username}>
									{viewModel.getUsername()}
								</Typography>
								<IconButton
									className={classes.editNameButton}
									onClick={() => {
										viewModel.setIsInput(true);
									}}>
									<img src='/img/editIcon.svg' alt='Edit Icon' className={classes.editNameButton} />
								</IconButton>
							</Grid>
						)}
						<Grid item className={classes.courseLeader}>
							<FormattedMessage id='profile.course_leader' />
						</Grid>
					</Grid>
				</Grid>
				<Divider variant='middle' />
				<Grid item xs={11} className={classes.buttonContainer}>
					<Button
						onClick={() => {
							viewModel.logout();
							router.push(Pages.REQUEST_LOGIN);
						}}
						className={classes.logout}
						classes={{ label: classes.label }}>
						<FormattedMessage id={'profile.logout'} />
					</Button>
				</Grid>
			</Popover>
		</>
	);
});

export default ProfileView;
